import { useRef } from "react";
import { useRecordContext, useUpdate, useNotify } from "react-admin";
import { Box, Button, Grid2 as Grid, IconButton, Typography } from "@mui/material";
import { CloudUpload as UploadIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { ResourceName } from "@/types";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { syncBrandingCache } from "@/utils/brandingSync";

const MAX_FILE_SIZE_KB = 256;
const ACCEPTED_TYPES = ["image/svg+xml", "image/png", "image/jpeg", "image/webp"];

/**
 * Converts a File to a data URI string via FileReader.
 */
const fileToDataUri = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

interface LogoCardProps {
  label: string;
  previewBg: string;
  dataUri: string | null | undefined;
  canEdit: boolean;
  onUpload: (dataUri: string) => void;
  onRemove: () => void;
  saving: boolean;
}

const LogoCard = ({ label, previewBg, dataUri, canEdit, onUpload, onRemove, saving }: LogoCardProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      alert("Please upload an SVG, PNG, JPEG, or WebP image.");
      return;
    }

    if (file.size > MAX_FILE_SIZE_KB * 1024) {
      alert(`File must be under ${MAX_FILE_SIZE_KB}KB. Current size: ${Math.round(file.size / 1024)}KB.`);
      return;
    }

    const uri = await fileToDataUri(file);
    onUpload(uri);

    // Reset input so re-uploading the same file triggers onChange
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {label}
      </Typography>

      {/* Preview */}
      <Box
        sx={{
          width: 160,
          height: 160,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: previewBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1,
          position: "relative",
        }}
      >
        {dataUri ? (
          <>
            <Box
              component="img"
              src={dataUri}
              alt={label}
              sx={{ maxWidth: 120, maxHeight: 120, objectFit: "contain" }}
            />
            {canEdit && (
              <IconButton
                size="small"
                onClick={onRemove}
                disabled={saving}
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  bgcolor: "background.paper",
                  "&:hover": { bgcolor: "error.light", color: "error.contrastText" },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </>
        ) : (
          <Typography variant="caption" color="text.secondary">
            No logo
          </Typography>
        )}
      </Box>

      {/* Upload button */}
      {canEdit && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept=".svg,.png,.jpg,.jpeg,.webp"
            style={{ display: "none" }}
            onChange={(e) => void handleFileChange(e)}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => inputRef.current?.click()}
            disabled={saving}
            sx={{ textTransform: "none" }}
          >
            {dataUri ? "Replace" : "Upload"}
          </Button>
        </>
      )}
    </Box>
  );
};

// ─── Main Section ────────────────────────────────────────────────────────────

export const LogoUploadSection = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const { canEdit } = useResourcePermissions(ResourceName.PARTNERS);
  const [update, { isLoading }] = useUpdate();

  if (!record) return null;

  // Logos are stored as top-level columns on the partners table, not inside configuration.branding
  const logoImage: string | null | undefined = record.logo_image;
  const logoImageDark: string | null | undefined = record.logo_image_dark;

  const saveLogo = (field: "logo_image" | "logo_image_dark", value: string | null) => {
    void update(
      ResourceName.PARTNERS,
      {
        id: record.id,
        data: { [field]: value },
        previousData: record,
      },
      {
        onSuccess: (data: Record<string, unknown>) => {
          notify(value ? "Logo uploaded" : "Logo removed", { type: "success" });
          syncBrandingCache(data);
        },
        onError: (error) => notifyError(error),
      }
    );
  };

  return (
    <Grid container spacing={4}>
      <Grid>
        <LogoCard
          label="Light Theme Logo"
          previewBg="#ffffff"
          dataUri={logoImage}
          canEdit={canEdit}
          saving={isLoading}
          onUpload={(uri) => saveLogo("logo_image", uri)}
          onRemove={() => saveLogo("logo_image", null)}
        />
      </Grid>
      <Grid>
        <LogoCard
          label="Dark Theme Logo"
          previewBg="#1a1a2e"
          dataUri={logoImageDark}
          canEdit={canEdit}
          saving={isLoading}
          onUpload={(uri) => saveLogo("logo_image_dark", uri)}
          onRemove={() => saveLogo("logo_image_dark", null)}
        />
      </Grid>
    </Grid>
  );
};
